import { haversineM } from './geo';
import type { TrackPoint } from './gpx';
import type { Fix } from './types';

export interface ReplayOptions {
  /** Position jitter amplitude and reported-accuracy inflation (metres). 0 = clean GPS. */
  noiseM: number;
  /** [startSec, endSec] windows during which no fixes are emitted (a tunnel). */
  dropoutRanges: [number, number][];
  /** Deterministic noise. */
  seed: number;
  /** Epoch ms of the first fix. */
  startEpochMs: number;
  /** Baseline reported accuracy (metres). */
  accuracyM: number;
}

const DEFAULTS: ReplayOptions = {
  noiseM: 0,
  dropoutRanges: [],
  seed: 1,
  startEpochMs: 1_700_000_000_000,
  accuracyM: 10,
};

/** Small deterministic PRNG (mulberry32). */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Turns a track into the fixes a phone would emit, with optional noise and tunnel dropouts.
 * Heading is deliberately left null so TrackFilter has to derive it, exactly as on a device
 * where the per-fix heading is -1 half the time.
 *
 * Position noise is correlated from fix to fix (a slow random walk of roughly ±noiseM/2),
 * the way real GPS error drifts; independent per-second jitter of 200 m would look like a
 * 400 m/s teleport and tells you nothing. Reported accuracy is inflated independently so
 * some fixes exceed the filter's accuracy cutoff.
 */
export function* replayFixes(track: TrackPoint[], opts: Partial<ReplayOptions> = {}): Generator<Fix> {
  const o = { ...DEFAULTS, ...opts };
  const rand = rng(o.seed);
  let offN = 0;
  let offE = 0;
  const step = (): void => {
    offN = offN * 0.95 + (rand() - 0.5) * 2 * o.noiseM * 0.15;
    offE = offE * 0.95 + (rand() - 0.5) * 2 * o.noiseM * 0.15;
  };

  for (let i = 0; i < track.length; i++) {
    const p = track[i]!;
    const sec = p.t / 1000;
    if (o.dropoutRanges.some(([s, e]) => sec >= s && sec <= e)) continue;

    let speed: number | null = null;
    if (i > 0) {
      const q = track[i - 1]!;
      const dt = (p.t - q.t) / 1000;
      if (dt > 0) speed = haversineM(q.lat, q.lon, p.lat, p.lon) / dt;
    }

    const noisy = o.noiseM > 0;
    if (noisy) step();
    yield {
      timestamp: o.startEpochMs + p.t,
      coords: {
        latitude: p.lat + (noisy ? offN / 111_000 : 0),
        longitude: p.lon + (noisy ? offE / (111_000 * Math.cos((p.lat * Math.PI) / 180)) : 0),
        accuracy: o.accuracyM + (noisy ? rand() * o.noiseM : 0),
        heading: null,
        speed,
      },
    };
  }
}
